import React from "react";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import { generateQuotationPDF } from "./Quotationpdfexport";
import ProductCatalogPanel from "./ProductCatalogPanel";
import SelectedProductsCart from "./SelectedProductsCart";
import NoteImagePairsEditor from "./NoteImagePairsEditor";

// The full create/edit-enquiry form: lead/client/priority fields, the
// product catalog + cart, PDF-only note/image pairs, and the footer
// actions (cancel / export quotation / submit). Rendered by <Enquiries />
// only while showCreateForm is true.
const EnquiryCreateForm = ({
  user,
  loading,
  editMode,
  editingEnquiryId,
  enquiryForm,
  setEnquiryForm,
  leads,
  clients,
  contactPersons,
  handleLeadChange,
  handleClientChange,
  setShowQuickAddLead,
  handleFloatInput,
  searchQuery,
  setSearchQuery,
  getFilteredProducts,
  getCategoryLevels,
  selectedCategoryPath,
  setSelectedCategoryPath,
  setAllProductCombinations,
  productCatalogRef,
  addProductToCart,
  calculateSellingPrice,
  updateProductQuantity,
  removeProductFromCart,
  updateProductPrice,
  updateProductTaxRate,
  addTopicToProduct,
  updateTopic,
  removeTopic,
  addActionToTopic,
  updateAction,
  removeAction,
  noteImagePairs,
  addNoteImagePair,
  updatePairNote,
  updatePairImage,
  removePairImage,
  removeNoteImagePair,
  setShowCreateForm,
  resetForm,
  createEnquiry,
}) => {
  // Only lock the Lead/Client selects while editing an enquiry we actually
  // resolved a lead/client id for. If prefill couldn't detect either (see
  // the console warning logged by mapEnquiryToFormState in the parent),
  // leave these open — otherwise the user would be stuck behind disabled
  // fields with no way to satisfy the "select a Lead or Client" validation.
  const lockLeadClient =
    editMode && Boolean(enquiryForm.leadId || enquiryForm.clientId);

  return (
    <div className="create-form-container">
      <div className="form-card">
        <div className="form-header">
          <h2>{editMode ? "Edit Enquiry" : "Create New Enquiry"}</h2>
          {editMode && (
            <span className="edit-badge">
              Editing Enquiry #{editingEnquiryId}
            </span>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            createEnquiry();
          }}
        >
          <>
            <div className="form-grid">
              <div className="form-group form-group-with-button">
                <label>Lead</label>
                <div className="input-with-action">
                  <select
                    value={enquiryForm.leadId || ""}
                    onChange={(e) => handleLeadChange(e.target.value)}
                    disabled={lockLeadClient || !!enquiryForm.clientId}
                  >
                    <option value="">-- Select Lead --</option>
                    {leads.map((lead) => (
                      <option key={lead.id} value={lead.id}>
                        {lead.companyName}
                      </option>
                    ))}
                  </select>
                  <ButtonWithIcon
                    icon="mdi:plus"
                    text=""
                    onClick={() => setShowQuickAddLead(true)}
                    className="submit-button btn-icon-only"
                    type="button"
                    title="Quick Add Lead"
                    disabled={lockLeadClient}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Client</label>
                <select
                  value={enquiryForm.clientId || ""}
                  onChange={(e) => handleClientChange(e.target.value)}
                  disabled={lockLeadClient || !!enquiryForm.leadId}
                >
                  <option value="">-- Select Client --</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.companyName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Contact Person</label>
                <select
                  value={enquiryForm.contactPersonId || ""}
                  onChange={(e) =>
                    setEnquiryForm((prev) => ({
                      ...prev,
                      contactPersonId: e.target.value
                        ? Number(e.target.value)
                        : null,
                    }))
                  }
                >
                  <option value="">-- Select Contact Person --</option>
                  {contactPersons.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.contactPersonName} ({contact.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Priority</label>
                <div
                  className="priority-btn-group"
                  style={{
                    borderRight: "1px solid #CBD5E0",
                    paddingRight: "10px",
                  }}
                >
                  {[
                    { value: "LOW", color: "#38A169" },
                    { value: "MEDIUM", color: "#D69E2E" },
                    { value: "HIGH", color: "#E53E3E" },
                  ].map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      style={{
                        background:
                          enquiryForm.priority === p.value
                            ? p.color
                            : "#E2E8F0",
                        color:
                          enquiryForm.priority === p.value
                            ? "white"
                            : "#2D3748",
                        padding: "6px 10px",
                        borderRadius: "20px",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      }}
                      onClick={() =>
                        setEnquiryForm((prev) => ({
                          ...prev,
                          priority: p.value,
                        }))
                      }
                    >
                      {p.value.charAt(0) + p.value.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Type</label>
                <div className="priority-btn-group">
                  {[
                    { value: "COLD", color: "#3182CE" },
                    { value: "WARM", color: "#D69E2E" },
                    { value: "HOT", color: "#E53E3E" },
                  ].map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      style={{
                        background:
                          enquiryForm.type === t.value ? t.color : "#E2E8F0",
                        color:
                          enquiryForm.type === t.value ? "white" : "#2D3748",
                        padding: "6px 10px",
                        borderRadius: "20px",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      }}
                      onClick={() =>
                        setEnquiryForm((prev) => ({
                          ...prev,
                          type: t.value,
                        }))
                      }
                    >
                      {t.value.charAt(0) + t.value.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Expected Close Date</label>
                <input
                  type="date"
                  value={enquiryForm.expectedCloseDate}
                  onChange={(e) =>
                    setEnquiryForm((prev) => ({
                      ...prev,
                      expectedCloseDate: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group">
                <label>Discount Percentage</label>
                <input
                  type="number"
                  step="0.01"
                  min=""
                  max="100"
                  value={enquiryForm.discountPercentage}
                  onChange={(e) =>
                    setEnquiryForm((prev) => ({
                      ...prev,
                      discountPercentage: handleFloatInput(e.target.value),
                    }))
                  }
                  placeholder="0.00"
                />
              </div>
            </div>
          </>

          {/* Product Selection Section */}
          <div className="products-section">
            <>
              <ProductCatalogPanel
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                getFilteredProducts={getFilteredProducts}
                getCategoryLevels={getCategoryLevels}
                selectedCategoryPath={selectedCategoryPath}
                setSelectedCategoryPath={setSelectedCategoryPath}
                setAllProductCombinations={setAllProductCombinations}
                productCatalogRef={productCatalogRef}
                enquiryForm={enquiryForm}
                addProductToCart={addProductToCart}
                calculateSellingPrice={calculateSellingPrice}
              />

              <SelectedProductsCart
                enquiryForm={enquiryForm}
                updateProductQuantity={updateProductQuantity}
                removeProductFromCart={removeProductFromCart}
                updateProductPrice={updateProductPrice}
                updateProductTaxRate={updateProductTaxRate}
                addTopicToProduct={addTopicToProduct}
                updateTopic={updateTopic}
                removeTopic={removeTopic}
                addActionToTopic={addActionToTopic}
                updateAction={updateAction}
                removeAction={removeAction}
              />
            </>
          </div>
          {/* ── MAIN NOTES ────────────────────────────────────────────────── */}
          <div className="form-notes-section">
            <label htmlFor="enquiry-notes">
              Notes{" "}
              <span className="notes-hint">
                (will appear at the bottom of the exported PDF as well as in the
                Enquiry Itself.)
              </span>
            </label>
            <textarea
              id="enquiry-notes"
              className="enquiry-notes-textarea"
              rows={5}
              placeholder="e.g. Prices valid for 30 days. GST as applicable. Delivery within 7–10 working days."
              value={enquiryForm.notes}
              onChange={(e) =>
                setEnquiryForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
            />
          </div>

          <NoteImagePairsEditor
            noteImagePairs={noteImagePairs}
            addNoteImagePair={addNoteImagePair}
            updatePairNote={updatePairNote}
            updatePairImage={updatePairImage}
            removePairImage={removePairImage}
            removeNoteImagePair={removeNoteImagePair}
          />

          <div className="form-footer">
            <ButtonWithIcon
              icon="mdi:cancel"
              text="Cancel"
              onClick={() => {
                setShowCreateForm(false);
                resetForm();
              }}
              className="cancel-button"
              type="button"
            />

            <ButtonWithIcon
              text="Export Quotation"
              onClick={() =>
                generateQuotationPDF({
                  enquiryData: enquiryForm,
                  companyName:
                    user?.companyName || user?.tenantName || "Your Company",
                  companyLogo:
                    user?.logo && user?.logoType
                      ? `data:${user.logoType};base64,${user.logo}`
                      : user?.companyLogo || null,
                  quotationNumber: editingEnquiryId
                    ? `ENQ-${editingEnquiryId}`
                    : "",
                  noteImagePairs,
                  taxBreakdown: enquiryForm.products.reduce((acc, p) => {
                    const price = p.customPrice
                      ? p.price || 0
                      : p._displayInfo?.sellingPrice || 0;
                    const qty = Number(p.quantity) || 0;
                    const rate = p.customPrice
                      ? (p.taxRate ?? 0)
                      : (p._displayInfo?.taxRate ?? 0);
                    if (rate > 0) {
                      acc[rate] = (acc[rate] || 0) + (price * qty * rate) / 100;
                    }
                    return acc;
                  }, {}),
                })
              }
              type="button"
              disabled={enquiryForm.products.length === 0}
              title="Generate PDF quotation"
            />

            <ButtonWithIcon
              icon={loading ? "mdi:loading" : "mdi:check"}
              text={
                loading
                  ? editMode
                    ? "Updating..."
                    : "Creating..."
                  : editMode
                    ? "Update Enquiry"
                    : "Create Enquiry"
              }
              disabled={loading}
              className="submit-button"
              type="submit"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnquiryCreateForm;
